import { apiClient } from '../client'
import type {
  AssetValue,
  AssetValueHistoryResponse,
  AssetValueInput,
  ValuationKind,
} from '../../app/modules/types/assetValue'

/**
 * Asset Valuation endpoints — `/api/v1/assets/{id}/value/` and
 * `/api/v1/ip/{id}/value/`.
 *
 * One factory rather than two hand-written clients: the contract is explicit
 * that the two differ ONLY in the base segment — same bodies, same codes, same
 * errors. Writing them twice is how the IP page ends up a version behind.
 *
 * Gotchas:
 * - **Trailing slashes are required.** Without one the backend 301s and the
 *   PUT body is **dropped** — a silent no-op save, not an error. `history/`
 *   is built off `base(id)` precisely so the slash cannot be lost.
 * - The IP segment is **singular `ip`**, matching the backend — not the
 *   `/ips` route and not the `ips` query-key scope.
 * - `apiClient`'s baseURL already ends in `/api/v1`. Paths start at `/assets/…`.
 * - **PUT, not PATCH** — full replacement. An omitted key is reset to `""`;
 *   an unknown key is a 400.
 * - An unchanged re-save is a server-side no-op: 200, nothing written, **no
 *   history row**. The dialog must prevent it.
 * - 404 is not an error: on GET it means *undeclared*, on DELETE it means
 *   *already cleared*.
 */
function valueApi(kind: ValuationKind) {
  const base = (id: string) => `/${kind}/${encodeURIComponent(id)}/value/`

  return {
    /**
     * Read the valuation on its own.
     *
     * **The pages do not use this** — the asset detail payload already carries
     * the same object under `value`, so rendering the card costs no extra
     * request. It exists because the endpoint does, and because a 404 here
     * means "undeclared", not "something went wrong": callers must render the
     * empty state, never an error toast.
     */
    get: async (id: string): Promise<AssetValue> => {
      const { data } = await apiClient.get(base(id))
      return data
    },

    /**
     * Declare or amend. `201` the first time an asset is valued, `200` every
     * time after — both are success, and the difference is the only thing
     * that distinguishes "Value declared." from "Value updated.", so the
     * status is returned alongside the body rather than thrown away.
     */
    put: async (
      id: string,
      body: AssetValueInput
    ): Promise<{ value: AssetValue; created: boolean }> => {
      const response = await apiClient.put(base(id), body)
      return { value: response.data, created: response.status === 201 }
    },

    /**
     * Clear it. `204` on success; `404` means there was nothing to clear,
     * which the caller treats as already-cleared. The valuation goes, the
     * history stays.
     */
    remove: async (id: string): Promise<void> => {
      await apiClient.delete(base(id))
    },

    /**
     * The change trail — newest first, standard DRF envelope, so `?page=` and
     * `?page_size=` apply. Keyed on the asset, so it outlives the valuation.
     */
    history: async (id: string, page = 1, pageSize = 20): Promise<AssetValueHistoryResponse> => {
      const { data } = await apiClient.get(`${base(id)}history/`, {
        params: { page, page_size: pageSize },
      })
      return data
    },
  }
}

export const assetValueApi = valueApi('assets')
export const ipValueApi = valueApi('ip')
export const fileValueApi = valueApi('files')

/** Pick the client for a resource kind — the pages are otherwise identical. */
export function valueApiFor(kind: ValuationKind) {
  return kind === 'ip' ? ipValueApi : kind === 'files' ? fileValueApi : assetValueApi
}
